import { useEffect, useRef, useState } from "react";
import { PlayCircle, CheckCircle2, AlertTriangle } from "lucide-react";
import { youtubeId } from "@/components/YouTubeBackground";

const ERRORS = {
  2: "That link does not contain a valid video ID. Copy the address straight from the YouTube share button.",
  5: "YouTube's player could not start this video in a browser.",
  100: "This video has been removed, or it is set to Private. Set it to Public or Unlisted.",
  101: "The video owner has turned off embedding on other websites, so it cannot be used as a hero background.",
  150: "The video owner has turned off embedding on other websites — or the network you are on is blocking YouTube. If this video plays fine on youtube.com, try this test again from a normal home or office connection.",
};

/**
 * Lets an admin confirm a hero YouTube link actually plays — and, when it does not,
 * see the real reason instead of silently getting the fallback poster on the live site.
 */
export default function YouTubeTester({ url, start = 0, end = 0, testId }) {
  const [state, setState] = useState("idle");
  const [message, setMessage] = useState("");
  const hostRef = useRef(null);
  const playerRef = useRef(null);

  useEffect(
    () => () => {
      try {
        playerRef.current?.destroy();
      } catch {
        /* already gone */
      }
    },
    []
  );

  const run = () => {
    const id = youtubeId(url);
    if (!id) {
      setState("error");
      setMessage(ERRORS[2]);
      return;
    }

    setState("testing");
    setMessage("Starting the player…");
    const host = hostRef.current;
    host.replaceChildren();
    const mount = document.createElement("div");
    host.appendChild(mount);

    try {
      playerRef.current?.destroy();
    } catch {
      /* already gone */
    }

    const build = (YT) => {
      playerRef.current = new YT.Player(mount, {
        videoId: id,
        width: 320,
        height: 180,
        host: "https://www.youtube-nocookie.com",
        playerVars: {
          autoplay: 1,
          mute: 1,
          controls: 0,
          enablejsapi: 1,
          playsinline: 1,
          origin: window.location.origin,
          start: start || 0,
          ...(end > start ? { end } : {}),
        },
        events: {
          onReady: (e) => e.target.playVideo(),
          onError: (e) => {
            setState("error");
            setMessage(ERRORS[e?.data] || `YouTube returned error code ${e?.data}.`);
          },
          onStateChange: (e) => {
            if (e.data === YT.PlayerState.PLAYING) {
              const trim = end > start ? ` Trimmed to ${start}s – ${end}s and looping.` : " Playing the full video on loop.";
              setState("ok");
              setMessage(`This link works as a hero background.${trim}`);
            }
          },
        },
      });
    };

    if (window.YT?.Player) {
      build(window.YT);
    } else {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof prev === "function") prev();
        build(window.YT);
      };
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(tag);
    }

    setTimeout(() => {
      setState((s) => {
        if (s !== "testing") return s;
        setMessage("The player did not start within 12 seconds. YouTube may be blocked on this network — try again from a different connection.");
        return "error";
      });
    }, 12000);
  };

  return (
    <div className="mt-2">
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" data-testid={`${testId}-run`} onClick={run} className="btn-outline h-9 px-4 text-xs">
          <PlayCircle className="h-3.5 w-3.5" />
          {state === "testing" ? "Testing…" : "Test this link"}
        </button>
        {state === "ok" && (
          <span data-testid={`${testId}-ok`} className="inline-flex items-start gap-2 text-xs text-primary">
            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {message}
          </span>
        )}
        {state === "error" && (
          <span data-testid={`${testId}-error`} className="inline-flex max-w-xl items-start gap-2 text-xs text-destructive">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {message}
          </span>
        )}
      </div>
      <div ref={hostRef} className={state === "ok" ? "mt-3 overflow-hidden rounded border border-border" : "hidden"} />
    </div>
  );
}
